import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Input, InputNumber, Select, Table, Modal, Space, Tag, message, Tabs, Divider, Row, Col, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import storageService from '../services/storageService';
import { logActivity } from '../services/auditService';
import { ACTIVITY_TYPES } from '../models/activityTypes';
import { SUBSCRIBER_TYPE_LABELS } from '../models/types';

const SettingsPage = () => {
  const { addTariff, updateTariff, deleteTariff } = useApp();
  const { user } = useAuth();
  const [tariffsLocal, setTariffsLocal] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // General settings state
  const [taxRate, setTaxRate] = useState();
  const [penaltyRate, setPenaltyRate] = useState();
  const [lateFeeThreshold, setLateFeeThreshold] = useState();
  const [paymentDueDays, setPaymentDueDays] = useState();
  const [appName, setAppName] = useState();
  const [appLogo, setAppLogo] = useState();
  const [appAddress, setAppAddress] = useState();
  const [appPhone, setAppPhone] = useState();
  const [appEmail, setAppEmail] = useState();

  // Toujours charger les paramètres ET les tarifs depuis la base à chaque affichage de la page
  useEffect(() => {
    const loadSettingsAndTariffs = async () => {
      const settings = await storageService.getSettings();
      setTaxRate(settings.tax_rate);
      setPenaltyRate(settings.penalty_rate);
      setLateFeeThreshold(settings.late_fee_threshold);
      setPaymentDueDays(settings.payment_due_days);
      setAppName(settings.app_name);
      setAppLogo(settings.app_logo);
      setAppAddress(settings.app_address);
      setAppPhone(settings.app_phone);
      setAppEmail(settings.app_email);
      // Charger les tarifs depuis la base
      const tariffsFromDb = await storageService.getTariffs();
      setTariffsLocal(tariffsFromDb);
    };
    loadSettingsAndTariffs();
    window.addEventListener('focus', loadSettingsAndTariffs);
    return () => {
      window.removeEventListener('focus', loadSettingsAndTariffs);
    };
  }, []);

  // Handle tariff form
  const handleAddTariff = () => {
    setIsEditMode(false);
    setSelectedTariff(null);
    form.resetFields();
    setIsFormOpen(true);
  };

  const handleEditTariff = (tariff) => {
    setIsEditMode(true);
    setSelectedTariff(tariff);
    // Convertir la date au format dayjs si elle existe
    const dateValue = tariff.effectiveDate ? dayjs(tariff.effectiveDate) : null;
    form.setFieldsValue({
      subscriberType: tariff.subscriberType,
      minVolume: tariff.minVolume,
      maxVolume: tariff.maxVolume,
      pricePerM3: tariff.pricePerM3,
      effectiveDate: dateValue
    });
    setIsFormOpen(true);
  };

  const handleTariffSubmit = async (values) => {
    try {
      setLoading(true);

      // Convertir la date dayjs en string ISO
      const valuesToSave = {
        ...values,
        effectiveDate: values.effectiveDate ? values.effectiveDate.format('YYYY-MM-DD') : new Date().toISOString().split('T')[0]
      };

      // Validation: Check if a tariff already exists for this subscriber type
      const existingTariff = isEditMode 
        ? tariffsLocal.find(t => t.id !== selectedTariff.id && t.subscriberType === valuesToSave.subscriberType)
        : tariffsLocal.find(t => t.subscriberType === valuesToSave.subscriberType);

      if (existingTariff) {
        message.error(`Un tarif existe déjà pour le type d'abonné "${SUBSCRIBER_TYPE_LABELS[valuesToSave.subscriberType]}". Un seul tarif par type d'abonné est autorisé.`);
        setLoading(false);
        return;
      }

      if (isEditMode && selectedTariff) {
        await updateTariff(selectedTariff.id, valuesToSave);
        
        // Log the activity
        if (user) {
          const changes = {};
          if (selectedTariff.subscriberType !== valuesToSave.subscriberType) 
            changes.subscriberType = { old: selectedTariff.subscriberType, new: valuesToSave.subscriberType };
          if (selectedTariff.minVolume !== valuesToSave.minVolume) 
            changes.minVolume = { old: selectedTariff.minVolume, new: valuesToSave.minVolume };
          if (selectedTariff.maxVolume !== valuesToSave.maxVolume) 
            changes.maxVolume = { old: selectedTariff.maxVolume, new: valuesToSave.maxVolume };
          if (selectedTariff.pricePerM3 !== valuesToSave.pricePerM3) 
            changes.pricePerM3 = { old: selectedTariff.pricePerM3, new: valuesToSave.pricePerM3 };
          if (selectedTariff.effectiveDate !== valuesToSave.effectiveDate) 
            changes.effectiveDate = { old: selectedTariff.effectiveDate, new: valuesToSave.effectiveDate };
          
          await logActivity(
            ACTIVITY_TYPES.SETTINGS_CHANGED,
            user.id,
            'tariff',
            selectedTariff.id,
            changes,
            `Tarif modifié: ${SUBSCRIBER_TYPE_LABELS[valuesToSave.subscriberType]}`
          );
        }
        
        message.success('Tarif modifié');
      } else {
        await addTariff(valuesToSave);
        
        // Log the activity
        if (user) {
          await logActivity(
            ACTIVITY_TYPES.SETTINGS_CHANGED,
            user.id,
            'tariff',
            `tariff-${Date.now()}`,
            valuesToSave,
            `Nouveau tarif ajouté: ${SUBSCRIBER_TYPE_LABELS[valuesToSave.subscriberType]}`
          );
        }
        
        message.success('Tarif ajouté');
      }

      // Reload tariffs from database to ensure sync
      const updatedTariffs = await storageService.getTariffs();
      setTariffsLocal(updatedTariffs);

      setIsFormOpen(false);
      form.resetFields();
    } catch (error) {
      message.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTariff = (id) => {
    Modal.confirm({
      title: 'Supprimer ce tarif ?',
      content: 'Cette action est irréversible',
      okText: 'Supprimer',
      cancelText: 'Annuler',
      okButtonProps: { danger: true },
      async onOk() {
        const tariffToDelete = tariffsLocal.find(t => t.id === id);
        await deleteTariff(id);
        
        // Log the activity
        if (user && tariffToDelete) {
          await logActivity(
            ACTIVITY_TYPES.SETTINGS_CHANGED,
            user.id,
            'tariff',
            id,
            tariffToDelete,
            `Tarif supprimé: ${SUBSCRIBER_TYPE_LABELS[tariffToDelete.subscriberType]}`
          );
        }
        
        // Reload tariffs from database
        const updatedTariffs = await storageService.getTariffs();
        setTariffsLocal(updatedTariffs);
        message.success('Tarif supprimé');
      }
    });
  };

  // Handle general settings
  const handleSaveTaxRate = async () => {
    await storageService.saveSetting('tax_rate', taxRate);
    
    // Log the activity
    if (user) {
      await logActivity(
        ACTIVITY_TYPES.SETTINGS_CHANGED,
        user.id,
        'settings',
        'tax_rate',
        { value: taxRate },
        `Taux de taxe changé à ${taxRate}%`
      );
    }
    
    message.success('Taux de taxe sauvegardé');
  };

  const handleSavePenaltyRate = async () => {
    await storageService.saveSetting('penalty_rate', penaltyRate);
    
    // Log the activity
    if (user) {
      await logActivity(
        ACTIVITY_TYPES.SETTINGS_CHANGED,
        user.id,
        'settings',
        'penalty_rate',
        { value: penaltyRate },
        `Taux de pénalité changé à ${penaltyRate}%`
      );
    }
    
    message.success('Taux de pénalité sauvegardé');
  };

  const handleSaveLateFeeThreshold = async () => {
    await storageService.saveSetting('late_fee_threshold', lateFeeThreshold);
    
    // Log the activity
    if (user) {
      await logActivity(
        ACTIVITY_TYPES.SETTINGS_CHANGED,
        user.id,
        'settings',
        'late_fee_threshold',
        { value: lateFeeThreshold },
        `Délai de pénalité changé à ${lateFeeThreshold} jours`
      );
    }
    
    message.success('Délai sauvegardé');
  };

  const handleSavePaymentDueDays = async () => {
    await storageService.saveSetting('payment_due_days', paymentDueDays);
    
    // Log the activity
    if (user) {
      await logActivity(
        ACTIVITY_TYPES.SETTINGS_CHANGED,
        user.id,
        'settings',
        'payment_due_days',
        { value: paymentDueDays },
        `Délai de paiement changé à ${paymentDueDays} jours`
      );
    }
    
    message.success('Délai de paiement sauvegardé');
  };

  const handleSaveAppName = async () => {
    await storageService.saveSetting('app_name', appName);
    
    // Log the activity
    if (user) {
      await logActivity(
        ACTIVITY_TYPES.SETTINGS_CHANGED,
        user.id,
        'settings',
        'app_name',
        { value: appName },
        `Nom de l'application changé à ${appName}`
      );
    }
    
    message.success('Nom de l\'application sauvegardé');
    // Recharger pour mettre à jour l'interface
    window.location.reload();
  };

  const handleSaveAppLogo = async () => {
    await storageService.saveSetting('app_logo', appLogo);
    
    // Log the activity
    if (user) {
      await logActivity(
        ACTIVITY_TYPES.SETTINGS_CHANGED,
        user.id,
        'settings',
        'app_logo',
        { value: appLogo },
        `Logo de l'application changé`
      );
    }
    
    message.success('Logo de l\'application sauvegardé');
    // Recharger pour mettre à jour l'interface
    window.location.reload();
  };

  const handleSaveAppAddress = async () => {
    await storageService.saveSetting('app_address', appAddress);
    
    // Log the activity
    if (user) {
      await logActivity(
        ACTIVITY_TYPES.SETTINGS_CHANGED,
        user.id,
        'settings',
        'app_address',
        { value: appAddress },
        `Adresse changeée`
      );
    }
    
    message.success('Adresse sauvegardée');
  };

  const handleSaveAppPhone = async () => {
    await storageService.saveSetting('app_phone', appPhone);
    
    // Log the activity
    if (user) {
      await logActivity(
        ACTIVITY_TYPES.SETTINGS_CHANGED,
        user.id,
        'settings',
        'app_phone',
        { value: appPhone },
        `Téléphone changé à ${appPhone}`
      );
    }
    
    message.success('Téléphone sauvegardé');
  };

  const handleSaveAppEmail = async () => {
    await storageService.saveSetting('app_email', appEmail);
    
    // Log the activity
    if (user) {
      await logActivity(
        ACTIVITY_TYPES.SETTINGS_CHANGED,
        user.id,
        'settings',
        'app_email',
        { value: appEmail },
        `Email changé à ${appEmail}`
      );
    }
    
    message.success('Email sauvegardé');
  };

  // Table columns for tariffs
  const tariffColumns = [
    {
      title: 'Type d\'abonné',
      dataIndex: 'subscriberType',
      key: 'subscriberType',
      render: (type) => SUBSCRIBER_TYPE_LABELS[type] || type
    },
    {
      title: 'Volume min (m³)',
      dataIndex: 'minVolume',
      key: 'minVolume',
      render: (volume) => `${volume?.toFixed(2) || '0.00'} m³`
    },
    {
      title: 'Volume max (m³)',
      dataIndex: 'maxVolume',
      key: 'maxVolume',
      render: (volume) => `${volume?.toFixed(2) || '0.00'} m³`
    },
    {
      title: 'Prix/m³ (FCFA)',
      dataIndex: 'pricePerM3',
      key: 'pricePerM3',
      render: (price) => `${price?.toFixed(4) || '0.0000'} FCFA`
    },
    {
      title: 'Actif depuis',
      dataIndex: 'effectiveDate',
      key: 'effectiveDate',
      render: (date) => date || '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditTariff(record)}
          >
            Modifier
          </Button>
          <Button
            type="primary"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteTariff(record.id)}
          >
            Supprimer
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '20px' }}>
      <h1>⚙️ Configuration du système</h1>

      <Tabs
        defaultActiveKey="1"
        items={[
          {
            key: '1',
            label: '📊 Tarifs',
            children: (
              <div>
                <Card style={{ marginBottom: '20px' }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddTariff}
                    style={{ marginBottom: '20px' }}
                  >
                    Ajouter un tarif
                  </Button>

                  <Table
                    columns={tariffColumns}
                    dataSource={tariffsLocal}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                  />
                </Card>
              </div>
            )
          },
          {
            key: '2',
            label: '💰 Paramètres financiers',
            children: (
              <div>
                <Card style={{ marginBottom: '20px' }}>
                  <h2>Taux de taxe</h2>
                  <Row gutter={16} align="middle" style={{ marginBottom: '20px' }}>
                    <Col span={8}>
                      <p style={{ marginBottom: '10px' }}>
                        Taux de taxe appliqué sur les factures (%)
                      </p>
                      <InputNumber
                        value={taxRate}
                        onChange={setTaxRate}
                        min={0}
                        max={100}
                        step={0.1}
                        style={{ width: '150px' }}
                      />
                    </Col>
                    <Col span={16}>
                      <Button
                        type="primary"
                        onClick={handleSaveTaxRate}
                        style={{ marginLeft: '10px' }}
                      >
                        Enregistrer
                      </Button>
                      <Tag style={{ marginLeft: '10px' }}>
                        Valeur actuelle: {taxRate}%
                      </Tag>
                    </Col>
                  </Row>
                </Card>

                <Card style={{ marginBottom: '20px' }}>
                  <h2>Pénalités de retard</h2>
                  <Row gutter={16} align="middle" style={{ marginBottom: '20px' }}>
                    <Col span={8}>
                      <p style={{ marginBottom: '10px' }}>
                        Taux de pénalité sur factures en retard (%)
                      </p>
                      <InputNumber
                        value={penaltyRate}
                        onChange={setPenaltyRate}
                        min={0}
                        max={100}
                        step={0.1}
                        style={{ width: '150px' }}
                      />
                    </Col>
                    <Col span={16}>
                      <Button
                        type="primary"
                        onClick={handleSavePenaltyRate}
                        style={{ marginLeft: '10px' }}
                      >
                        Enregistrer
                      </Button>
                      <Tag style={{ marginLeft: '10px' }}>
                        Valeur actuelle: {penaltyRate}%
                      </Tag>
                    </Col>
                  </Row>
                </Card>

                <Card>
                  <h2>Délai d'application des pénalités</h2>
                  <Row gutter={16} align="middle" style={{ marginBottom: '20px' }}>
                    <Col span={8}>
                      <p style={{ marginBottom: '10px' }}>
                        Nombre de jours avant application de pénalités
                      </p>
                      <InputNumber
                        value={lateFeeThreshold}
                        onChange={setLateFeeThreshold}
                        min={1}
                        max={365}
                        step={1}
                        style={{ width: '150px' }}
                      />
                    </Col>
                    <Col span={16}>
                      <Button
                        type="primary"
                        onClick={handleSaveLateFeeThreshold}
                        style={{ marginLeft: '10px' }}
                      >
                        Enregistrer
                      </Button>
                      <Tag style={{ marginLeft: '10px' }}>
                        Valeur actuelle: {lateFeeThreshold} jours
                      </Tag>
                    </Col>
                  </Row>
                </Card>

                <Card>
                  <h2>Délai de paiement des factures</h2>
                  <Row gutter={16} align="middle" style={{ marginBottom: '20px' }}>
                    <Col span={8}>
                      <p style={{ marginBottom: '10px' }}>
                        Nombre de jours pour payer la facture
                      </p>
                      <InputNumber
                        value={paymentDueDays}
                        onChange={setPaymentDueDays}
                        min={1}
                        max={365}
                        step={1}
                        style={{ width: '150px' }}
                      />
                    </Col>
                    <Col span={16}>
                      <Button
                        type="primary"
                        onClick={handleSavePaymentDueDays}
                        style={{ marginLeft: '10px' }}
                      >
                        Enregistrer
                      </Button>
                      <Tag style={{ marginLeft: '10px' }}>
                        Valeur actuelle: {paymentDueDays} jours
                      </Tag>
                    </Col>
                  </Row>
                </Card>
              </div>
            )
          },
          {
            key: '3',
            label: '📋 Résumé des tarifs',
            children: (
              <div>
                <Card>
                  <h2>Tarifs actuels par type d'abonné</h2>
                  <Divider />
                  {['individual', 'professional', 'administration'].map((type) => {
                    const typeTariffs = tariffsLocal.filter(t => t.subscriberType === type);
                    return (
                      <div key={type} style={{ marginBottom: '30px' }}>
                        <Tag color="blue" style={{ fontSize: '14px', padding: '5px 10px' }}>
                          {SUBSCRIBER_TYPE_LABELS[type]}
                        </Tag>
                        {typeTariffs.length > 0 ? (
                          <Table
                            columns={[
                              { title: 'Prix/m³', dataIndex: 'pricePerM3', render: (v) => `${v.toFixed(2)} FCFA` }
                            ]}
                            dataSource={typeTariffs}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            style={{ marginTop: '10px' }}
                          />
                        ) : (
                          <p style={{ marginTop: '10px', color: '#999' }}>
                            Aucun tarif configuré pour ce type
                          </p>
                        )}
                      </div>
                    );
                  })}
                </Card>
              </div>
            )
          },
          {
            key: '4',
            label: '🎨 Branding',
            children: (
              <div>
                <Card style={{ marginBottom: '20px' }}>
                  <h2>Nom de l'application</h2>
                  <Row gutter={16} align="middle" style={{ marginBottom: '20px' }}>
                    <Col span={8}>
                      <p style={{ marginBottom: '10px' }}>
                        Nom affiché dans l'interface
                      </p>
                      <Input
                        value={appName}
                        onChange={(e) => setAppName(e.target.value)}
                        placeholder="Ex: Gestion EAU"
                        style={{ width: '100%' }}
                      />
                    </Col>
                    <Col span={16}>
                      <Button
                        type="primary"
                        onClick={handleSaveAppName}
                        style={{ marginLeft: '10px' }}
                      >
                        Enregistrer
                      </Button>
                      <Tag style={{ marginLeft: '10px' }}>
                        Valeur actuelle: {appName}
                      </Tag>
                    </Col>
                  </Row>
                </Card>

                <Card>
                  <h2>URL du logo</h2>
                  <Row gutter={16} align="middle" style={{ marginBottom: '20px' }}>
                    <Col span={8}>
                      <p style={{ marginBottom: '10px' }}>
                        URL de l'image du logo (laisser vide pour aucun logo)
                      </p>
                      <Input
                        value={appLogo}
                        onChange={(e) => setAppLogo(e.target.value)}
                        placeholder="Ex: https://example.com/logo.png"
                        style={{ width: '100%' }}
                      />
                    </Col>
                    <Col span={16}>
                      <Button
                        type="primary"
                        onClick={handleSaveAppLogo}
                        style={{ marginLeft: '10px' }}
                      >
                        Enregistrer
                      </Button>
                      {appLogo && (
                        <div style={{ marginTop: '10px' }}>
                          <p>Aperçu du logo:</p>
                          <img src={appLogo} alt="Logo" style={{ maxHeight: '50px', maxWidth: '200px' }} />
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card>

                <Card style={{ marginBottom: '20px' }}>
                  <h2>Adresse</h2>
                  <Row gutter={16} align="middle" style={{ marginBottom: '20px' }}>
                    <Col span={8}>
                      <p style={{ marginBottom: '10px' }}>
                        Adresse de l'organisation
                      </p>
                      <Input.TextArea
                        value={appAddress}
                        onChange={(e) => setAppAddress(e.target.value)}
                        placeholder="Ex: 123 rue de l'Eau, 75000 Paris"
                        rows={3}
                      />
                    </Col>
                    <Col span={16}>
                      <Button
                        type="primary"
                        onClick={handleSaveAppAddress}
                        style={{ marginLeft: '10px' }}
                      >
                        Enregistrer
                      </Button>
                    </Col>
                  </Row>
                </Card>

                <Card style={{ marginBottom: '20px' }}>
                  <h2>Téléphone</h2>
                  <Row gutter={16} align="middle" style={{ marginBottom: '20px' }}>
                    <Col span={8}>
                      <p style={{ marginBottom: '10px' }}>
                        Numéro de téléphone
                      </p>
                      <Input
                        value={appPhone}
                        onChange={(e) => setAppPhone(e.target.value)}
                        placeholder="Ex: +33 1 23 45 67 89"
                      />
                    </Col>
                    <Col span={16}>
                      <Button
                        type="primary"
                        onClick={handleSaveAppPhone}
                        style={{ marginLeft: '10px' }}
                      >
                        Enregistrer
                      </Button>
                    </Col>
                  </Row>
                </Card>

                <Card style={{ marginBottom: '20px' }}>
                  <h2>Email</h2>
                  <Row gutter={16} align="middle" style={{ marginBottom: '20px' }}>
                    <Col span={8}>
                      <p style={{ marginBottom: '10px' }}>
                        Adresse email
                      </p>
                      <Input
                        value={appEmail}
                        onChange={(e) => setAppEmail(e.target.value)}
                        placeholder="Ex: contact@example.com"
                        type="email"
                      />
                    </Col>
                    <Col span={16}>
                      <Button
                        type="primary"
                        onClick={handleSaveAppEmail}
                        style={{ marginLeft: '10px' }}
                      >
                        Enregistrer
                      </Button>
                    </Col>
                  </Row>
                </Card>
              </div>
            )
          }
        ]}
      />

      {/* Modal de formulaire pour tarifs */}
      <Modal
        title={isEditMode ? 'Modifier un tarif' : 'Ajouter un tarif'}
        open={isFormOpen}
        onOk={() => form.submit()}
        onCancel={() => setIsFormOpen(false)}
        okText={isEditMode ? 'Mettre à jour' : 'Ajouter'}
        cancelText="Annuler"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleTariffSubmit}
        >
          <Form.Item
            label="Type d'abonné"
            name="subscriberType"
            rules={[{ required: true, message: 'Le type est obligatoire' }]}
          >
            <Select
              placeholder="Sélectionner un type"
              options={[
                { label: SUBSCRIBER_TYPE_LABELS.individual, value: 'individual' },
                { label: SUBSCRIBER_TYPE_LABELS.professional, value: 'professional' },
                { label: SUBSCRIBER_TYPE_LABELS.administration, value: 'administration' }
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Volume minimum (m³)"
            name="minVolume"
            rules={[{ required: true, message: 'Le volume minimum est obligatoire' }]}
          >
            <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Volume maximum (m³)"
            name="maxVolume"
            rules={[{ required: true, message: 'Le volume maximum est obligatoire' }]}
          >
            <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Prix par m³ (FCFA)"
            name="pricePerM3"
            rules={[{ required: true, message: 'Le prix est obligatoire' }]}
          >
            <InputNumber min={0} step={0.01} precision={4} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Date d'application"
            name="effectiveDate"
            rules={[{ required: true, message: 'La date est obligatoire' }]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SettingsPage;
